// components/filePreview/FilePreviewModal.tsx
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { FilePreviewData } from "../../hooks/useFilePreview";
import { PdfRenderer } from "./renderers/PdfRenderer";
import { ImageRenderer } from "./renderers/ImageRenderer";
import { XlsxRenderer } from "./renderers/XlsxRenderer";
import { DocRenderer } from "./renderers/DocRenderer";

interface Props {
  open: boolean;
  loading: boolean;
  error: string | null;
  previewData: FilePreviewData | null;
  onClose: () => void;
}

const renderContent = (previewData: FilePreviewData) => {
  switch (previewData.type) {
    case "pdf":
      return <PdfRenderer blobUrl={previewData.blobUrl} />;
    case "image":
      return <ImageRenderer blobUrl={previewData.blobUrl} />;
    case "xlsx":
      return <XlsxRenderer arrayBuffer={previewData.rawArrayBuffer!} />;
    case "doc":
      return <DocRenderer arrayBuffer={previewData.rawArrayBuffer!} />;
    default:
      return (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">
            Preview not available for this file type.
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Please download the file to view it.
          </Typography>
        </Box>
      );
  }
};

export const FilePreviewModal = ({
  open,
  loading,
  error,
  previewData,
  onClose,
}: Props) => {
  const handleDownload = () => {
    if (!previewData) return;
    const a = document.createElement("a");
    a.href = previewData.blobUrl;
    a.download = previewData.fileName;
    a.click();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, minHeight: 300 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* DialogTitle already renders as <h2>. Override the element so the
            Typography keeps the h6 visual style but renders as <span> —
            a heading tag nested inside <h2> is invalid HTML. */}
        <Typography
          variant="h6"
          component="span"
          noWrap
          title={previewData?.fileName ?? "File Preview"}
          sx={{ maxWidth: "80%" }}
        >
          {previewData?.fileName ?? "File Preview"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Loading state */}
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Error state */}
        {!loading && error && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {/* Preview content */}
        {!loading && !error && previewData && renderContent(previewData)}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="text">
          Close
        </Button>
        {previewData && (
          <Button
            onClick={handleDownload}
            startIcon={<DownloadIcon />}
            variant="outlined"
          >
            Download
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
